'use client';

import { useState } from 'react';

const t = {
  en: { title: 'Embed this calculator on your site', hint: 'Copy the code below. It includes a link back to this page.', copy: 'Copy code', copied: 'Copied!' },
  ar: { title: 'ضع هذه الحاسبة في موقعك', hint: 'انسخ الكود أدناه. يتضمن رابطًا يعود إلى هذه الصفحة.', copy: 'نسخ الكود', copied: 'تم النسخ!' },
} as const;

interface EmbedSnippetProps {
  locale: string;
  /** Absolute URL of the embeddable (iframe) version. */
  embedUrl: string;
  /** Absolute URL of the tool page — the backlink. */
  toolUrl: string;
  toolName: string;
}

const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

export default function EmbedSnippet({ locale, embedUrl, toolUrl, toolName }: EmbedSnippetProps) {
  const labels = t[locale === 'ar' ? 'ar' : 'en'];
  const [copied, setCopied] = useState(false);

  const code =
    `<iframe src="${embedUrl}" title="${escapeAttr(toolName)}" width="100%" height="780" ` +
    `style="border:0;max-width:960px" loading="lazy"></iframe>\n` +
    `<p><a href="${toolUrl}">${toolName} — Mohamed Saber</a></p>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the code stays selectable in the textarea */
    }
  };

  return (
    <section aria-labelledby="embed-heading" className="rounded-2xl border border-(--border) bg-(--surface) p-5 sm:p-7">
      <h2 id="embed-heading" className="text-xl font-bold text-(--text)">{labels.title}</h2>
      <p className="mt-1 text-sm text-(--muted)">{labels.hint}</p>
      <textarea
        readOnly
        dir="ltr"
        rows={5}
        value={code}
        onFocus={(e) => e.currentTarget.select()}
        className="mt-4 w-full resize-none rounded-lg border border-(--border) bg-(--bg) p-3 font-mono text-xs text-(--muted)"
      />
      <button
        type="button"
        onClick={copy}
        className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-neutral-900 hover:bg-primary-600 transition-colors"
      >
        {copied ? labels.copied : labels.copy}
      </button>
    </section>
  );
}
